import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import { Button, Card } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  cardVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

const defaultFeatures = [
  'Automated backups',
  '24/7 support',
  'Unlimited projects',
  'Unlimited users',
  'Custom domain',
  'Custom branding',
  'Advanced analytics',
  'Custom permissions',
  'Advanced reports',
]

/**
 * Props for PricingPanel component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Single pricing tier with muted background
 * - Features displayed in grid below the card
 * - Best for simple "one plan fits all" pricing
 */
export interface PricingPanelProps {
  /** Main heading text */
  heading?: string
  /** Description text below heading */
  description?: string
  /** Price display (e.g., "16/month") */
  price?: string
  /** Price prefix (e.g., "$") */
  pricePrefix?: string
  /** Subtext below price */
  priceSubtext?: string
  /** Button text */
  buttonText?: string
  /** Button URL */
  buttonUrl?: string
  /** Section title above features */
  featuresTitle?: string
  /** List of features */
  features?: string[]
  /** Additional CSS classes */
  className?: string
}

/**
 * PricingPanel - Single tier pricing with feature grid
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────┐
 * │              Heading                   │
 * │            Description                 │
 * │         ┌─────────────┐                │
 * │         │  Starting at│                │
 * │         │   $16/mo    │                │
 * │         │  [Button]   │                │
 * │         └─────────────┘                │
 * │     What's included in the plan        │
 * │  ✓ Feature   ✓ Feature   ✓ Feature    │
 * │  ✓ Feature   ✓ Feature   ✓ Feature    │
 * └────────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Simple single-plan pricing
 * - SaaS products with one tier
 * - Free trial promotions
 *
 * @example
 * ```tsx
 * <PricingPanel
 *   heading="Simple Pricing"
 *   description="One plan, everything included."
 *   price="29/month"
 *   features={['Unlimited users', '24/7 support', 'API access']}
 * />
 * ```
 */
const PricingPanel = ({
  heading = 'Pricing',
  description = 'Everything you need to build and scale your product, with a simple pricing plan.',
  price = '16/month',
  pricePrefix = '$',
  priceSubtext = 'With a 7-day free trial',
  buttonText = 'Start Free Trial',
  buttonUrl = '#',
  featuresTitle = "What's included in the plan",
  features = defaultFeatures,
  className,
}: PricingPanelProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const cardAnimation = prefersReducedMotion ? reducedMotionVariants : cardVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('bg-muted py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto max-w-7xl">
        <motion.div
          className="flex flex-col items-center gap-4 text-center"
          variants={headerAnimation}
        >
          <h2 className="mb-2 text-3xl font-semibold lg:text-4xl">{heading}</h2>
          <div className="max-w-lg"><Markdown size="base">{description}</Markdown></div>
        </motion.div>
        <div className="mt-10">
          <motion.div variants={cardAnimation}>
            <Card className="mx-auto flex w-full max-w-sm flex-col justify-between gap-10 rounded-[var(--radius)] p-6 text-center">
              <p className="text-2xl">Starting at</p>
              <div>
                <div className="flex justify-center">
                  <span className="text-lg">{pricePrefix}</span>
                  <span className="text-4xl font-semibold lg:text-5xl">{price}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{priceSubtext}</p>
              </div>
              <Button className="w-full" size="lg" asChild>
                <a href={buttonUrl}>{buttonText}</a>
              </Button>
            </Card>
          </motion.div>
          <motion.p
            className="mt-8 text-center text-xl font-semibold lg:mt-10 lg:text-2xl"
            variants={headerAnimation}
          >
            {featuresTitle}
          </motion.p>
          <motion.ul
            className="mx-auto mt-4 grid w-fit md:w-auto md:max-w-2xl md:grid-cols-2 md:pl-14 lg:max-w-4xl lg:grid-cols-3"
            variants={gridContainerVariants(0.05, 0.3)}
          >
            {features.map((feature) => (
              <motion.li
                key={feature}
                className="mt-4 flex items-center gap-2 text-sm font-medium"
                variants={itemAnimation}
              >
                <Check className="size-5" />
                <span className="text-muted-foreground">{feature}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </motion.section>
  )
}

export { PricingPanel }
